import React, { useState } from "react";
import { BookOpen, ChevronDown, ChevronUp, ExternalLink, Printer } from "lucide-react";
import { DIARY_CODES, DIARY_CODE_GROUPS, legacyCodesByCode, readDiaryCode, reminderForDiaryCode } from "@/lib/diaryCodes";

// Every place a code is shown reads src/lib/diaryCodes.js: the diaries' notes and lists, the
// printable sheet, the reminders and the billing claim suggestion. Change a code there and
// all of them follow.

const FLAG_STYLES = {
  "Extension check": "bg-orange-100 text-orange-800",
  "New grant": "bg-rose-100 text-rose-800",
};

function ReminderFlag({ entry }) {
  const flag = reminderForDiaryCode(entry);
  if (!flag) return null;
  return <span className={`ml-1.5 inline-block whitespace-nowrap rounded-full px-1.5 text-[10px] font-semibold ${FLAG_STYLES[flag]}`}>{flag}</span>;
}

// Under a diary row's listing: what its code means, or that it is not one of the universal
// codes (an old firm code is read, with the code to use instead).
export function DiaryCodeNote({ text, clientName = "x", className = "" }) {
  if (!String(text || "").trim() || !String(clientName || "").trim()) return null;
  const read = readDiaryCode(text);
  if (read.unknown.length) {
    return (
      <p className={`text-[10px] font-semibold text-amber-800 ${className}`}>
        Not a diary code: {read.unknown.join(", ")}
      </p>
    );
  }
  if (read.legacy.length) {
    return (
      <p className={`text-[10px] text-slate-600 ${className}`}>
        Old code {read.legacy.map((item) => `${item.written} → use ${item.code}`).join(", ")}
      </p>
    );
  }
  return <p className={`text-[10px] text-slate-500 ${className}`}>{read.codes.map((entry) => entry.label).join(" + ")}</p>;
}

function CodeTable({ group }) {
  return (
    <table className="w-full text-left text-xs">
      <tbody>
        {DIARY_CODES.filter((entry) => entry.group === group).map((entry) => (
          <tr key={entry.code} className="border-t border-slate-200 align-top">
            <td className="w-16 py-1 pr-2"><code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono font-bold text-slate-900">{entry.code}</code></td>
            <td className="py-1 pr-2 font-medium text-slate-800">{entry.label}<ReminderFlag entry={entry} /></td>
            <td className="py-1 text-slate-500">{entry.fee || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// The list of codes inside a tool that reads them, with a link to the printable sheet.
export function DiaryCodesLegend({ sheetHref = "", className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <section className={`rounded-xl border border-slate-200 bg-white ${className}`}>
      <div className="flex items-center justify-between gap-2 px-4 py-2.5">
        <button type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open} className="flex flex-1 items-center justify-between gap-2 text-left">
          <span className="flex items-center gap-2 text-sm font-bold text-slate-800"><BookOpen className="h-4 w-4 text-slate-600" /> Diary codes</span>
          <span className="flex items-center gap-1 text-xs font-semibold text-slate-500">{open ? "Hide" : "Type one of these for each listing"}{open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</span>
        </button>
        {sheetHref && (
          <a href={sheetHref} className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:border-slate-400">
            Printable sheet <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
      {open && (
        <div className="grid gap-3 border-t border-slate-100 px-4 py-3 md:grid-cols-2">
          <p className="text-xs text-slate-600 md:col-span-2">The same at every firm. A listing with two events: <strong>PLEA/CMEN</strong>. Single or consolidated rates come from the grant.</p>
          {DIARY_CODE_GROUPS.map((group) => (
            <div key={group}>
              <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{group}</h3>
              <CodeTable group={group} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// The whole list as a one-page sheet for staff, printable from the portal.
export function DiaryCodesSheet({ brand = "" }) {
  const firm = brand === "vals" ? "VALS" : brand === "lacw" ? "LACW" : "LACW & VALS";
  return (
    <div className="diary-codes-sheet mx-auto max-w-4xl space-y-5 px-4 py-6 text-slate-900 print:max-w-none print:space-y-3 print:p-0">
      <style>{"@media print { .diary-codes-sheet { zoom: 0.74; } }"}</style>
      <div className="flex flex-wrap items-end justify-between gap-3 border-b-2 border-slate-900 pb-3">
        <div className="max-w-2xl space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-sky-800">{firm} weekly diary</p>
          <h1 className="text-3xl font-bold print:text-2xl">Diary codes</h1>
          <p className="text-sm text-slate-600">Type one of these codes for each listing in the diary. They are the same at every firm, and each one names the VLA fee the listing uses up, so the portal can tell when an extension or a new grant is needed.</p>
        </div>
        <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-700 print:hidden">
          <Printer className="h-4 w-4" /> Print
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        {[
          ["One code per listing", <>Capitals, as written here: <code className="font-mono font-bold">CMEN</code>, not &ldquo;cont mention&rdquo;.</>],
          ["Two events? Use a slash", <>A plea at the same listing as a contest mention: <code className="font-mono font-bold">PLEA/CMEN</code>.</>],
          ["No consolidated codes", "Single or consolidated rates come from the grant, not the code."],
        ].map(([title, text]) => (
          <div key={title} className="rounded-md border border-slate-200 px-3 py-2">
            <p className="text-xs font-semibold">{title}</p>
            <p className="text-xs text-slate-600">{text}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-x-6 gap-y-4 md:grid-cols-2 print:grid-cols-2">
        {DIARY_CODE_GROUPS.map((group) => (
          <section key={group} className="break-inside-avoid">
            <h2 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{group}</h2>
            <CodeTable group={group} />
          </section>
        ))}
      </div>

      <div className="grid gap-6 md:grid-cols-[1.3fr_1fr] print:grid-cols-[1.3fr_1fr]">
        <section>
          <h2 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Old codes to stop using</h2>
          <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
            {legacyCodesByCode().map(({ code, old }) => (
              <li key={code} className="whitespace-nowrap">
                {old.map((item) => <span key={item} className="mr-1 font-mono font-semibold text-slate-500 line-through">{item}</span>)}→ <code className="rounded bg-slate-100 px-1 font-mono font-bold">{code}</code>
              </li>
            ))}
          </ul>
          <p className="mt-1.5 text-xs text-slate-600">The portal still reads these in older weeks and shows the code to use instead. <strong className="text-slate-900">CM now means committal mention</strong>, not contest mention.</p>
        </section>
        <section className="space-y-1.5 text-xs text-slate-600">
          <h2 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">What the flags mean</h2>
          <p><span className={`mr-1 rounded-full px-1.5 text-[10px] font-semibold ${FLAG_STYLES["Extension check"]}`}>Extension check</span>The listing may be beyond what the first grant covers. The portal reminds you to check the grant and apply for an extension in time.</p>
          <p><span className={`mr-1 rounded-full px-1.5 text-[10px] font-semibold ${FLAG_STYLES["New grant"]}`}>New grant</span>An appeal needs its own grant.</p>
          <p>Anything else in the listing column (RoD, FVCD, D, …) shows as &ldquo;Not a diary code&rdquo; in the portal.</p>
        </section>
      </div>
    </div>
  );
}
